/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useMenu } from "@app/vue/providers/Menu";
import { useRoute, useRouter } from "vue-router";
import { getRestaurantImageURL } from "@lemmonhq/blogic";
import { QRScannerModal } from "../ShopHomeV2/components/QRScannerModal";
import { createGroupOrder } from "@app/server/client";
import { cn, useLocalization } from "@lemmonhq/uikit";
import { calculateAllowedOrderServiceMethods } from "@lemmonhq/orders";
import { ShopNav } from "../Shop/components/ShopNav";

export const ShopHomePage = defineComponent({
  name: "ShopHomePage",
  setup() {
    const { shop, cart } = useMenu();
    const route = useRoute();
    const router = useRouter();
    const { t } = useLocalization();
    const showQRModal = ref(false);
    const loadingGroupOrder = ref(false);

    const shopSlug = route.params.shopslug as string;

    const handleOnlineOrder = async () => {
      try {
        loadingGroupOrder.value = true;
        const groupOrder = await createGroupOrder(shop.value.id);
        router.push(`/restaurant/${shopSlug}/group-order/${groupOrder.id}`);
      } finally {
        loadingGroupOrder.value = false;
      }
    };

    const handleTableOrder = () => {
      const lastTableId = localStorage.getItem(
        `CURRENT_TABLE_ORDER/${shop.value.id}`,
      );
      if (lastTableId) {
        //router.push(`/restaurant/${shopSlug}/group-order/${lastTableId}`);
      } else {
      }
      showQRModal.value = true;
    };

    const handleReserveTable = () => {
      router.push(`/restaurant/${shopSlug}/reserve/tables`);
    };

    const handleYourOrders = () => {
      router.push(`/restaurant/${shopSlug}/account/orders`);
    };

    const handleLoyalty = () => {
      router.push(`/restaurant/${shopSlug}/account/loyalty`);
    };

    const onQRScan = (url: string) => {
      showQRModal.value = false;
      window.location.href = url;
    };

    return () => {
      if (!shop.value) return null;
      const allowedOrderServiceMethods = calculateAllowedOrderServiceMethods(
        shop.value,
      );
      const hasOnlineOrder = allowedOrderServiceMethods?.some((method) =>
        ["takeaway", "delivery"].includes(method),
      );

      return (
        <>
          <ShopNav />
          <div class="overflow-hidden ">
            {/* Restaurant Logo Section */}
            <div class=" h-64 md:h-80 ">
              <div
                style={`background-image: url("${getRestaurantImageURL(
                  shop.value,
                )}");`}
                class="w-full h-full bg-no-repeat bg-cover bg-center flex justify-center items-center py-8"
              >
                <div class="bg-[rgba(255,255,255,0.8)] mx-auto px-4 py-8 sm:py-12 rounded-3xl rounded rounded-xl shadow-xl border">
                  <div class="text-center">
                    {/* Success Icon */}

                    <h1 class="text-3xl text-accent sm:text-4xl font-bold mb-3">
                      {shop.value.name}
                    </h1>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Options Grid */}
            <div class="px-6 py-8 max-w-4xl mx-auto">
              <h2 class="text-2xl font-semibold text-center mb-8 text-base-content">
                {t("customer.shop_home.what_would_you_like")}
              </h2>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Online Order Option */}
                {hasOnlineOrder && (
                  <div
                    onClick={handleOnlineOrder}
                    class={cn(
                      "group cursor-pointer bg-white dark:bg-base-300 rounded-2xl p-6 shadow-lg hover:shadow-xl border",
                      loadingGroupOrder.value &&
                        "opacity-50 pointer-events-none",
                    )}
                  >
                    <div class="text-center">
                      <div class="w-16 h-16 mx-auto mb-4 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <span class="material-symbols-outlined text-3xl text-primary">
                          shopping_bag
                        </span>
                      </div>
                      <h3 class="text-xl font-semibold mb-2 text-base-content">
                        {t("customer.shop_home.online_order")}
                      </h3>
                      <p class="text-base-content/70 text-sm mb-3">
                        {t("customer.shop_home.order_pickup_delivery")}
                      </p>
                      <div class="flex flex-wrap gap-1 justify-center">
                        {cart.cart.allowedOrderServiceMethods?.includes(
                          "takeaway",
                        ) && (
                          <span class="badge badge-primary badge-sm">
                            {t("customer.shop_home.takeaway")}
                          </span>
                        )}
                        {cart.cart.allowedOrderServiceMethods?.includes(
                          "delivery",
                        ) && (
                          <span class="badge badge-primary badge-sm">
                            {t("customer.shop_home.delivery")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Table Order Option */}
                <div
                  onClick={handleTableOrder}
                  class="group cursor-pointer bg-white dark:bg-base-300 rounded-2xl p-6 shadow-lg hover:shadow-xl border"
                >
                  <div class="text-center">
                    <div class="w-16 h-16 mx-auto mb-4 bg-accent/10 rounded-full flex items-center justify-center group-hover:bg-accent/20 transition-colors">
                      <span class="material-symbols-outlined text-3xl text-accent">
                        qr_code_scanner
                      </span>
                    </div>
                    <h3 class="text-xl font-semibold mb-2 text-base-content">
                      {t("customer.shop_home.table_order")}
                    </h3>
                    <p class="text-base-content/70 text-sm">
                      {t("customer.shop_home.scan_qr_to_order")}
                    </p>
                  </div>
                </div>

                {/* Reserve Table Option */}
                <div
                  onClick={handleReserveTable}
                  style={
                    import.meta.env.VITE_ENV === "staging"
                      ? ""
                      : "display: hidden;"
                  }
                  class="group cursor-pointer bg-white dark:bg-base-300 rounded-2xl p-6 shadow-lg hover:shadow-xl border"
                >
                  <div class="text-center">
                    <div class="w-16 h-16 mx-auto mb-4 bg-secondary/10 rounded-full flex items-center justify-center group-hover:bg-secondary/20 transition-colors">
                      <span class="material-symbols-outlined text-3xl text-secondary">
                        event_seat
                      </span>
                    </div>
                    <h3 class="text-xl font-semibold mb-2 text-base-content">
                      {t("customer.shop_home.reserve_table")}
                    </h3>
                    <p class="text-base-content/70 text-sm">
                      {t("customer.shop_home.book_table")}
                    </p>
                  </div>
                </div>

                {/* Your Orders Option */}
                <div
                  onClick={handleYourOrders}
                  class="group cursor-pointer bg-white dark:bg-base-300 rounded-2xl p-6 shadow-lg hover:shadow-xl border"
                >
                  <div class="text-center">
                    <div class="w-16 h-16 mx-auto mb-4 bg-info/10 rounded-full flex items-center justify-center group-hover:bg-info/20 transition-colors">
                      <span class="material-symbols-outlined text-3xl text-info">
                        receipt_long
                      </span>
                    </div>
                    <h3 class="text-xl font-semibold mb-2 text-base-content">
                      {t("customer.shop_home.your_orders")}
                    </h3>
                    <p class="text-base-content/70 text-sm">
                      {t("customer.shop_home.view_order_history")}
                    </p>
                  </div>
                </div>

                {/* Loyalty and Points Option */}
                <div
                  onClick={handleLoyalty}
                  class="group cursor-pointer bg-white dark:bg-base-300 rounded-2xl p-6 shadow-lg hover:shadow-xl border"
                >
                  <div class="text-center">
                    <div class="w-16 h-16 mx-auto mb-4 bg-warning/10 rounded-full flex items-center justify-center group-hover:bg-warning/20 transition-colors">
                      <span class="material-symbols-outlined text-3xl text-warning">
                        stars
                      </span>
                    </div>
                    <h3 class="text-xl font-semibold mb-2 text-base-content">
                      {t("customer.shop_home.loyalty_points")}
                    </h3>
                    <p class="text-base-content/70 text-sm">
                      {t("customer.shop_home.check_rewards")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* QR Scanner Modal */}
            {showQRModal.value && (
              <QRScannerModal
                onClose={() => (showQRModal.value = false)}
                onScan={onQRScan}
              />
            )}
          </div>
        </>
      );
    };
  },
});
